/**
 * Client-side cookie sync utility
 * Syncs the info_idp cookie from secureserver.net to custom domains
 * by calling the intersite-sync/info-idp endpoint
 */

/**
 * Check if info_idp cookie exists
 * @returns {boolean} True if cookie exists
 */
function hasInfoIdpCookie() {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.cookie.includes('info_idp=');
}

/**
 * Set cookie on the current domain
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {string} domain - Cookie domain
 * @param {Object} options - Additional cookie options
 */
function setCookie(name, value, domain, options = {}) {
  const {
    path = '/',
    secure = true,
    sameSite = 'None',
    maxAge = 86400 // 24 hours default
  } = options;

  let cookieString = `${name}=${encodeURIComponent(value)}; path=${path}`;

  // Append optional cookie attributes using array iteration
  [
    domain && `; domain=${domain}`,
    secure && '; secure',
    sameSite && `; samesite=${sameSite}`,
    maxAge && `; max-age=${maxAge}`
  ].forEach(str => str && (cookieString += str));

  document.cookie = cookieString;
}

/**
 * Remove info_idp cookie from the current domain
 * @returns {boolean} True if cookie was removed
 */
export function removeInfoIdpCookie() {
  if (typeof document === 'undefined') {
    return false;
  }

  // Get the current domain
  const hostname = window.location.hostname;

  // Remove cookie for the current domain
  // Setting max-age to 0 removes the cookie
  document.cookie = `info_idp=; path=/; domain=${hostname}; max-age=0; secure; samesite=None`;

  // Also try without domain attribute for cases where it might be set that way
  document.cookie = `info_idp=; path=/; max-age=0; secure; samesite=None`;

  return true;
}

/**
 * Check if current domain is secureserver.net
 * @returns {boolean} True if on secureserver.net domain
 */
export function isSecureServerDomain() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  // Check if hostname ends with secureserver.net
  return hostname.endsWith('secureserver.net');
}

/**
 * Sync info_idp cookie from secureserver.net to custom domain
 * @param {string} storefrontDomain - Domain of the storefront (e.g., www.dev-secureserver.net)
 * @returns {Promise<boolean>} - True if sync was successful
 */
// eslint-disable-next-line max-statements
export async function syncInfoIdpCookie(storefrontDomain) {
  // Skip if not in browser
  if (typeof window === 'undefined') {
    return false;
  }

  // Skip if already on secureserver.net domain (cookie is already there)
  if (isSecureServerDomain()) {
    return true;
  }

  // Skip if cookie already exists
  if (hasInfoIdpCookie()) {
    return true;
  }

  // Skip if no domain configured
  if (!storefrontDomain) {
    return false;
  }

  const syncUrl = `${storefrontDomain}/api/v1/intersite-sync/info-idp`;

  try {
    const response = await fetch(syncUrl, {
      method: 'GET',
      credentials: 'include', // Important: send cookies with the request
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.error('Failed to sync info_idp cookie: HTTP', response.status);

      // Remove the cookie if we get 401 or 404 (user not authenticated or not found)
      if (response.status === 401 || response.status === 404) {
        removeInfoIdpCookie();
      }

      return false;
    }

    // Parse the JSON response
    const data = await response.json();

    if (!data.info_idp) {
      // eslint-disable-next-line no-console
      console.error('No info_idp found in response');
      return false;
    }

    // Set the cookie on the current domain
    // Use the domain from response if available, otherwise use current domain
    const cookieDomain = data.domain || window.location.hostname;

    setCookie('info_idp', data.info_idp, cookieDomain, {
      secure: true,
      sameSite: 'None',
      maxAge: 86400 // 24 hours
    });

    return true;

  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error syncing info_idp cookie:', error);
    return false;
  }
}

// For CommonJS compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    syncInfoIdpCookie,
    removeInfoIdpCookie,
    isSecureServerDomain
  };
}
